import React, { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { employeesApi, EmployeeWithHours } from '../services/api';
import ResolveHoursForm from '../components/ResolveHoursForm';

const ResolveHoursPage: React.FC = () => {
  usePageTitle('Resolve Hours');
  const [employees, setEmployees] = useState<EmployeeWithHours[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [departmentFilter, setDepartmentFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<'name' | 'hours' | 'earnings' | 'paid' | 'due' | 'resolved' | 'unpaid'>('hours');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeWithHours | null>(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetchEmployeesWithHours();
  }, []);

  const fetchEmployeesWithHours = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await employeesApi.getWithHours();
      setEmployees(response.data.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load employee data');
      console.error('Error fetching employees with hours:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResolveHours = (employee: EmployeeWithHours) => {
    setSelectedEmployee(employee);
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setSelectedEmployee(null);
    fetchEmployeesWithHours(); // Refresh data
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setSelectedEmployee(null);
  };

  // Filter and sort employees
  const filteredEmployees = employees
    .filter(emp => {
      const matchesSearch = emp.full_legal_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (emp.preferred_name && emp.preferred_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                          emp.position.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = !statusFilter || emp.status === statusFilter;
      const matchesDepartment = !departmentFilter || emp.department === departmentFilter;
      
      return matchesSearch && matchesStatus && matchesDepartment;
    })
    .sort((a, b) => {
      let aValue: number | string;
      let bValue: number | string;
      
      switch (sortBy) {
        case 'name':
          aValue = a.full_legal_name;
          bValue = b.full_legal_name;
          break;
        case 'hours':
          aValue = a.total_hours;
          bValue = b.total_hours;
          break;
        case 'earnings':
          aValue = a.total_earnings;
          bValue = b.total_earnings;
          break;
        case 'paid':
          aValue = a.total_paid;
          bValue = b.total_paid;
          break;
        case 'due':
          aValue = a.total_due;
          bValue = b.total_due;
          break;
        case 'resolved':
          aValue = a.resolved_hours;
          bValue = b.resolved_hours;
          break;
        case 'unpaid':
          aValue = a.unpaid_hours;
          bValue = b.unpaid_hours;
          break;
        default:
          return 0;
      }
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'desc' ? bValue.localeCompare(aValue) : aValue.localeCompare(bValue);
      } else {
        return sortOrder === 'desc' ? (bValue as number) - (aValue as number) : (aValue as number) - (bValue as number);
      }
    });

  // Calculate statistics
  const totalEmployees = employees.length;
  const totalHours = employees.reduce((sum, emp) => sum + emp.total_hours, 0);
  const totalEarnings = employees.reduce((sum, emp) => sum + emp.total_earnings, 0);
  const totalPaid = employees.reduce((sum, emp) => sum + emp.total_paid, 0);
  const totalDue = employees.reduce((sum, emp) => sum + emp.total_due, 0);

  // Get unique departments for filter
  const uniqueDepartments = Array.from(new Set(employees.map(emp => emp.department))).sort();

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error Loading Data</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
              <div className="mt-4">
                <button
                  onClick={fetchEmployeesWithHours}
                  className="px-4 py-2 bg-red-100 text-red-800 rounded-md hover:bg-red-200"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Employee Hours Overview</h1>
            <p className="text-gray-600">Review all employees and their total work hours</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={fetchEmployeesWithHours}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Refresh Data
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Employees</p>
                <p className="text-2xl font-semibold text-gray-900">{totalEmployees}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Hours</p>
                <p className="text-2xl font-semibold text-gray-900">{totalHours.toFixed(2)}h</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Earnings</p>
                <p className="text-2xl font-semibold text-gray-900">${totalEarnings.toFixed(2)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Paid</p>
                <p className="text-2xl font-semibold text-green-600">${totalPaid.toFixed(2)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Due</p>
                <p className="text-2xl font-semibold text-red-600">${totalDue.toFixed(2)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filter and Search Section */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Filter & Search</h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <input
                type="text"
                id="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or position..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                id="status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label htmlFor="department" className="block text-sm font-medium text-gray-700 mb-1">
                Department
              </label>
              <select
                id="department"
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Departments</option>
                {uniqueDepartments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="sortBy" className="block text-sm font-medium text-gray-700 mb-1">
                Sort By
              </label>
              <select
                id="sortBy"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'name' | 'hours' | 'earnings' | 'paid' | 'due' | 'resolved' | 'unpaid')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="hours">Total Hours</option>
                <option value="resolved">Resolved Hours</option>
                <option value="unpaid">Unpaid Hours</option>
                <option value="earnings">Total Earnings</option>
                <option value="paid">Total Paid</option>
                <option value="due">Total Due</option>
                <option value="name">Name</option>
              </select>
            </div>
            <div>
              <label htmlFor="sortOrder" className="block text-sm font-medium text-gray-700 mb-1">
                Sort Order
              </label>
              <select
                id="sortOrder"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="desc">High to Low</option>
                <option value="asc">Low to High</option>
              </select>
            </div>
          </div>
        </div>

        {/* Employees Table */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">
              Employees ({filteredEmployees.length})
            </h2>
          </div>
          <div className="overflow-x-auto lg:overflow-x-visible">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Employee
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Position & Dept
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Status
                  </th>
                  <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Total Hrs
                  </th>
                  <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Resolved
                  </th>
                  <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Unpaid
                  </th>
                  <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Earnings
                  </th>
                  <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Paid
                  </th>
                  <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Due
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredEmployees.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-6 py-12 text-center text-gray-500">
                      No employees found matching the current filters.
                    </td>
                  </tr>
                ) : (
                  filteredEmployees.map((employee) => (
                    <tr key={employee.id} className="hover:bg-gray-50">
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                              <span className="text-xs font-medium text-blue-600">
                                {employee.full_legal_name.split(' ').map((n: string) => n[0]).join('')}
                              </span>
                            </div>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">{employee.full_legal_name}</div>
                            {employee.preferred_name && (
                              <div className="text-xs text-gray-500">"{employee.preferred_name}"</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{employee.position}</div>
                        <div className="text-xs text-gray-500">{employee.department}</div>
                      </td>
                      <td className="px-2 py-3 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          employee.status === 'active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {employee.status}
                        </span>
                      </td>
                      <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900 font-semibold text-right">
                        {employee.total_hours.toFixed(2)}h
                      </td>
                      <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900 font-semibold text-blue-600 text-right">
                        {employee.resolved_hours.toFixed(2)}h
                      </td>
                      <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900 font-semibold text-orange-600 text-right">
                        {employee.unpaid_hours.toFixed(2)}h
                      </td>
                      <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900 font-semibold text-right">
                        ${employee.total_earnings.toFixed(2)}
                      </td>
                      <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900 font-semibold text-green-600 text-right">
                        ${employee.total_paid.toFixed(2)}
                      </td>
                      <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900 font-semibold text-red-600 text-right">
                        ${employee.total_due.toFixed(2)}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-center">
                        <button 
                          onClick={() => handleResolveHours(employee)}
                          className="text-blue-600 hover:text-blue-900 transition-colors"
                        >
                          Resolve
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary */}
        {filteredEmployees.length > 0 && (
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-7 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {filteredEmployees.reduce((sum, emp) => sum + emp.total_hours, 0).toFixed(2)}h
                </div>
                <div className="text-sm text-gray-600">Total Hours</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-600">
                  {filteredEmployees.reduce((sum, emp) => sum + emp.resolved_hours, 0).toFixed(2)}h
                </div>
                <div className="text-sm text-gray-600">Resolved Hours</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-orange-600">
                  {filteredEmployees.reduce((sum, emp) => sum + emp.unpaid_hours, 0).toFixed(2)}h
                </div>
                <div className="text-sm text-gray-600">Unpaid Hours</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  ${filteredEmployees.reduce((sum, emp) => sum + emp.total_earnings, 0).toFixed(2)}
                </div>
                <div className="text-sm text-gray-600">Total Earnings</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">
                  ${filteredEmployees.reduce((sum, emp) => sum + emp.total_paid, 0).toFixed(2)}
                </div>
                <div className="text-sm text-gray-600">Total Paid</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-600">
                  ${filteredEmployees.reduce((sum, emp) => sum + emp.total_due, 0).toFixed(2)}
                </div>
                <div className="text-sm text-gray-600">Total Due</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {filteredEmployees.length}
                </div>
                <div className="text-sm text-gray-600">Employees</div>
              </div>
            </div>
          </div>
        )}

        {/* Resolve Hours Form Modal */}
        {showForm && selectedEmployee && (
          <ResolveHoursForm
            employee={selectedEmployee}
            onSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
          />
        )}
      </div>
    </div>
  );
};

export default ResolveHoursPage;
